/// <reference types="google.maps" />
import { Component, ElementRef, OnInit, ViewChild, inject, signal, computed, AfterViewInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Ubicacion } from 'src/app/models/Ubicacion';
import { UsuarioService } from 'src/app/services/usuario.service';
import { MAP_ID } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';

@Component({
    selector: 'app-my-account-ubicacion',
    templateUrl: './my-account-ubicacion.component.html',
    styleUrls: ['./my-account-ubicacion.component.css'],
    standalone: false
})
export class MyAccountUbicacionComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('divMap') divMap!: ElementRef<HTMLElement>;
  @ViewChild('inputPlaces') inputPlaces!: ElementRef<HTMLInputElement>;

  private _router = inject(Router);
  private _aRoute = inject(ActivatedRoute);
  private _location = inject(Location);
  private _fb = inject(FormBuilder);

  private _usuarioService = inject(UsuarioService);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);

  // ------- estado -------
  error = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  // id (0 => agregar)
  id = Number(this._aRoute.snapshot.paramMap.get('id') ?? 0);

  operacion = signal<string>('Agregar ');
  botonOperacion = signal<string>('Agregar ');

  id_perfil = signal<number>(0);

  // defaults (si agregás)
  n_latitud = signal<number>(-34.75989677508757);
  n_longitud = signal<number>(-58.406636762250805);

  map: any;
  private autocomplete?: google.maps.places.Autocomplete;
  private marker?: any;

  // Form
  myAccountUbicacion = this._fb.nonNullable.group({
    domicilio: ['', Validators.required],
    piso_dpto: [''],
    codigo_postal: ['', Validators.required],
    telefono: ['', [Validators.required, Validators.pattern(/^[0-9]*$/)]],
  });

  // -------- Solo números en teléfono --------
  soloNumeros(controlName: 'telefono', event: Event) {
    const input = event.target as HTMLInputElement;
    const filtrado = input.value.replace(/\D/g, '');
    if (filtrado !== input.value) {
      this.myAccountUbicacion.get(controlName)?.setValue(filtrado);
    }
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this._router.navigate(['/sign-in']);
        return;
      }

      this.id_perfil.set(this._usuarioService.usuario().id_perfil ?? 0);

      if (this.id && this.id !== 0) {
        this.operacion.set('Editar ');
        this.botonOperacion.set('Actualizar ');
      } else {
        this.operacion.set('Agregar ');
        this.botonOperacion.set('Agregar ');
      }
    });
  }

  async ngAfterViewInit(): Promise<void> {
    // Importante: esperar Google Maps (web + capacitor)
    await this._loadingService.run(async () => {
      try {
        await this.waitForGoogleMaps();
      } catch (e: any) {
        console.error(e);
        this.error.set('No se pudo cargar Google Maps. Revisá la API Key / restricciones.');
        return;
      }

      if (this.id && this.id !== 0) {
        // EDITAR
        try {
          const res = await firstValueFrom(this._usuarioService.getUbicacion(this.id));

          if (!res) {
            // No existe => no tiene sentido editar
            this.error.set('La ubicación no existe o fue eliminado.');
            this._router.navigate(['/my-account-ubicaciones']);
            return;
          }

          this.myAccountUbicacion.patchValue({
            domicilio: res.x_domicilio ?? '',
            piso_dpto: res.x_piso_dpto ?? '',
            codigo_postal: res.x_codigo_postal ?? '',
            telefono: res.x_telefono ?? '',
          });

          this.n_latitud.set(res.n_latitud ?? this.n_latitud());
          this.n_longitud.set(res.n_longitud ?? this.n_longitud());

          await this.cargarMapa(this.n_latitud(), this.n_longitud());
          await this.cargarAutocomplete();
        } catch (e) {
          console.error(e);
          this.error.set('No se obtuvieron los datos de la ubicación.');
        }
      } else {
        // AGREGAR
        await this.cargarMapa(this.n_latitud(), this.n_longitud());
        await this.ubicacionActual(); // intenta centrar en la ubicación actual
        await this.cargarAutocomplete();
      }
    });
  }

  ngOnDestroy(): void {
    // limpiar listeners del autocomplete si querés (no crítico)
    this.autocomplete = undefined;
    this.map = undefined;
    this.marker = undefined;
  }

  back() {
    this._location.back();
  }

  // ---------------------------
  // Google Maps load helper
  // ---------------------------
  private async waitForGoogleMaps(timeoutMs = 12000): Promise<void> {
    const start = Date.now();
    while (!(window as any).google?.maps?.importLibrary) {
      if (Date.now() - start > timeoutMs) {
        throw new Error('Google Maps no cargó a tiempo.');
      }
      await new Promise((r) => setTimeout(r, 50));
    }
  }

  // ---------------------------
  // Ubicación actual + geocode
  // ---------------------------
  async ubicacionActual(): Promise<void> {
    await this._loadingService.run(async () => {
      try {
        const position = await this._usuarioService.getCurrentPosition();
        const { latitude, longitude } = position.coords;

        // Geocode (opcional, solo log)
        try {
          const { Geocoder } = (await google.maps.importLibrary('geocoding')) as google.maps.GeocodingLibrary;
          const geocoder = new Geocoder();
          const response = await geocoder.geocode({ location: { lat: latitude, lng: longitude } });

          if (response.results?.[0]) {
            const direccion = response.results[0].formatted_address;
            // opcional: podrías setear domicilio si querés
            // this.myAccountUbicacion.patchValue({ domicilio: direccion });
            console.log('📌 Dirección aproximada:', direccion);
          }
        } catch (e) {
          console.warn('⚠️ No se pudo geocodificar:', e);
        }

        this.n_latitud.set(latitude);
        this.n_longitud.set(longitude);

        await this.cargarMapa(latitude, longitude);
      } catch (error) {
        console.error('❌ Error al obtener ubicación:', error);
        // no bloqueamos el flujo: el mapa ya está con default
      }
    });
  }

  // ---------------------------
  // Autocomplete
  // ---------------------------
  private async cargarAutocomplete(): Promise<void> {
    await google.maps.importLibrary('places');

    if (!this.inputPlaces?.nativeElement) return;

    // evita duplicar listeners si esto se llama más de una vez
    if (this.autocomplete) return;

    this.autocomplete = new google.maps.places.Autocomplete(this.inputPlaces.nativeElement, {
      componentRestrictions: { country: ['AR'] },
      fields: ['address_components', 'geometry'],
      types: ['address'],
    });

    google.maps.event.addListener(this.autocomplete, 'place_changed', () => {
      const place: any = this.autocomplete!.getPlace();
      if (!place?.geometry?.location) return;

      let direccionTemp = '';
      let localidadTemp = '';
      let codPostalTemp = '';
      let numeroTemp = '';

      (place.address_components || []).forEach((item: any) => {
        if (item.types.includes('street_number')) {
          numeroTemp = ' ' + item.long_name;
        }
        if (item.types.includes('route')) {
          direccionTemp = item.long_name + numeroTemp;
        }
        if (item.types[0] === 'locality') {
          localidadTemp = item.short_name;
          direccionTemp += ', ' + item.short_name;
        }
        if (item.types[0] === 'administrative_area_level_1' && localidadTemp !== 'CABA') {
          direccionTemp += ', ' + item.long_name;
        }
        if (item.types.includes('postal_code')) {
          codPostalTemp = item.long_name;
        }
      });

      this.myAccountUbicacion.patchValue({
        domicilio: direccionTemp,
        codigo_postal: codPostalTemp,
      });

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      this.n_latitud.set(lat);
      this.n_longitud.set(lng);

      void this.cargarMapa(lat, lng);
    });
  }

  // ---------------------------
  // Mapa + marker
  // ---------------------------
  async cargarMapa(latitude: number, longitude: number): Promise<void> {
    const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    const el = this.divMap?.nativeElement;
    if (!el) return;

    // crear mapa solo una vez
    if (!this.map) {
      this.map = new Map(el, {
        center: { lat: latitude, lng: longitude },
        zoom: 17,
        mapId: MAP_ID,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
      });

      this.marker = new AdvancedMarkerElement({
        map: this.map,
        position: { lat: latitude, lng: longitude },
        title: 'Ubicación',
      });

      return;
    }

    // reutilizar mapa/marker
    this.map.setCenter({ lat: latitude, lng: longitude });
    if (this.marker) this.marker.position = { lat: latitude, lng: longitude };
  }

  // ---------------------------
  // Guardar
  // ---------------------------
  async saveUbicacion(): Promise<void> {
    this.error.set('');

    // validaciones "tipo tus pantallas"
    const v = this.myAccountUbicacion.getRawValue();

    if (!v.domicilio) {
      this.error.set('Debe Completar el Domicilio');
      return;
    }
    if (!v.codigo_postal) {
      this.error.set('Debe Completar el Código Postal');
      return;
    }
    if (!v.telefono) {
      this.error.set('Debe Completar el Teléfono');
      return;
    }
    if (!/^[0-9]*$/.test(v.telefono)) {
      this.error.set('El Teléfono debe contener solo números');
      return;
    }

    if (this.myAccountUbicacion.invalid) {
      this.myAccountUbicacion.markAllAsTouched();
      this.error.set('Por favor, completá los campos requeridos.');
      return;
    }

    await this._loadingService.run(async () => {
      const ubicacion: Ubicacion = {
        id_usuario: this._usuarioService.usuario().id,
        x_domicilio: v.domicilio,
        x_piso_dpto: v.piso_dpto ?? '',
        x_codigo_postal: v.codigo_postal,
        x_telefono: v.telefono,
        n_latitud: this.n_latitud(),
        n_longitud: this.n_longitud(),
      };

      try {
        if (this.id && this.id !== 0) {
          ubicacion.id = this.id;
          await firstValueFrom(this._usuarioService.updateUbicacion(ubicacion));
          this.back();
        } else {
          // si no hay default, marcarla
          if (this._usuarioService.ubicacion_default.getValue().x_domicilio === '') {
            ubicacion.b_default = true;
            this._usuarioService.ubicacion_default.next(ubicacion);
          }

          await firstValueFrom(this._usuarioService.createUbicacion(ubicacion));
          this.back();
        }
      } catch (e) {
        console.error(e);
        this.error.set(this.id && this.id !== 0 ? 'Error al actualizar la Ubicación' : 'Error al agregar la Ubicación');
      }
    });
  }
}